const AIEmployeesPost = () => {
  const metadata = {
    id: 2,
    slug: "trusted-enterprise-ai-agents",
    title: "Beyond “AI Employees”: Building Enterprise Agents That Can Be Trusted",
    date: "19 July 2026",
    dateISO: "2026-07-19",
    dateModifiedISO: "2026-07-19",
    excerpt:
      "A formal, risk-bounded account of enterprise AI agents: constrained authority, durable state, recoverable execution, system-level evaluation, and accountable human oversight.",
    readTime: "10 min read",
    category: "AI & Automation",
    image:
      "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
  };

  const content = (
    <div className="content">
      <h2>Trustworthy Enterprise AI Agents as Controlled Systems</h2>

      <h3>Abstract</h3>
      <p>
        Enterprise AI agents couple probabilistic models to retrieval, memory,
        and tools that alter organizational systems. Their salient property is
        therefore not conversational fluency but delegated authority under
        uncertainty. This article develops a compact system model and argues
        that trustworthiness is an end-to-end property produced by bounded
        autonomy, independently enforced permissions, explicit state,
        recoverable execution, adversarial evaluation, operational
        observability, and accountable human control. It does not assume that
        model outputs are correct, instructions are benign, tools are
        available, or reviewers are infallible. The governing principle is
        separation: a model may propose an action, but deterministic and
        organizational controls decide whether it is authorized, executable,
        and reversible.
      </p>

      <h3>Research Question and Scope</h3>
      <p>
        <strong>Research question:</strong> Under what technical and
        organizational conditions can an enterprise delegate sequential
        workflow decisions to an AI agent while keeping the probability and
        impact of unauthorized, erroneous, or irrecoverable action within an
        explicitly accepted risk envelope? This is a systems question, not a
        claim that a model can be certified as “trusted.” The unit of analysis
        is one workflow with a trigger, accountable owner, authorized outcomes,
        terminal states, and defined case population.
      </p>

      <h3>Formal System Model</h3>
      <p>
        Model an agentic workflow as a partially observed controlled process
        <em> M = (A, E, T, P, B, S)</em>. The agent <em>A</em> maps an
        observation and internal context to a proposed action. The environment
        <em> E</em> contains users, enterprise records, external content, and
        services; it is non-stationary and may be adversarial. Tools
        <em> T</em> are typed interfaces with observable preconditions and
        effects. Policy <em>P</em> maps identity, case, proposed operation, and
        current state to permit, deny, or require-review. The authority boundary
        <em> B</em> is the maximum set of effects available under issued
        credentials and hard business constraints. Durable state <em>S</em>
        records workflow status, evidence, approvals, tool results, and
        recovery metadata. At step <em>t</em>, the model proposes
        <em> a<sub>t</sub></em>; only a reference monitor outside the model may
        authorize and dispatch it.
      </p>
      <p>
        A concise risk decomposition is
        <em> R = Σ<sub>h</sub> Pr(h | x, v, c) × I(h) × X(h)</em>, over hazards
        <em> h</em>. Here <em>x</em> is the case distribution, <em>v</em> the
        deployed component versions, <em>c</em> the control configuration,
        <em>I</em> impact, and <em>X</em> exposure. Hazards arise from perception
        or retrieval error, planning error, unauthorized tool selection,
        malformed arguments, control failure, state corruption, dependency
        failure, and ineffective human intervention. The expression structures
        analysis; it does not imply precise probabilities where evidence is
        sparse. Severe hazards also require categorical release gates.
      </p>

      <figure className="research-figure">
        <svg
          className="research-figure__svg"
          viewBox="0 0 800 360"
          role="img"
          aria-labelledby="controlled-agent-title controlled-agent-desc"
        >
          <title id="controlled-agent-title">
            Controlled enterprise agent architecture
          </title>
          <desc id="controlled-agent-desc">
            The model proposes actions inside a bounded workflow. A policy
            gateway and optional human approval sit at the authority boundary
            before tools. Durable state and telemetry observe every stage.
          </desc>
          <defs>
            <marker
              id="controlled-agent-arrow"
              markerWidth="10"
              markerHeight="10"
              refX="8"
              refY="3"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M0,0 L0,6 L9,3 z" />
            </marker>
          </defs>

          <rect className="research-figure__box" x="35" y="65" width="155" height="74" rx="8" />
          <text className="research-figure__label" x="112" y="96" textAnchor="middle">
            Agent model
          </text>
          <text className="research-figure__muted" x="112" y="119" textAnchor="middle">
            proposes, never grants
          </text>

          <rect className="research-figure__box" x="240" y="65" width="155" height="74" rx="8" />
          <text className="research-figure__label" x="317" y="96" textAnchor="middle">
            Workflow engine
          </text>
          <text className="research-figure__muted" x="317" y="119" textAnchor="middle">
            validates sequence
          </text>

          <rect className="research-figure__box" x="445" y="45" width="155" height="114" rx="8" />
          <text className="research-figure__label" x="522" y="82" textAnchor="middle">
            Policy gateway
          </text>
          <text className="research-figure__muted" x="522" y="105" textAnchor="middle">
            identity · scope · limits
          </text>
          <text className="research-figure__muted" x="522" y="128" textAnchor="middle">
            human approval if required
          </text>

          <rect className="research-figure__box" x="650" y="65" width="115" height="74" rx="8" />
          <text className="research-figure__label" x="707" y="96" textAnchor="middle">
            Tools
          </text>
          <text className="research-figure__muted" x="707" y="119" textAnchor="middle">
            typed effects
          </text>

          <line
            className="research-figure__line"
            x1="190"
            y1="102"
            x2="232"
            y2="102"
            markerEnd="url(#controlled-agent-arrow)"
          />
          <line
            className="research-figure__line"
            x1="395"
            y1="102"
            x2="437"
            y2="102"
            markerEnd="url(#controlled-agent-arrow)"
          />
          <line
            className="research-figure__line"
            x1="600"
            y1="102"
            x2="642"
            y2="102"
            markerEnd="url(#controlled-agent-arrow)"
          />
          <line
            className="research-figure__line"
            x1="420"
            y1="22"
            x2="420"
            y2="178"
            strokeDasharray="8 6"
          />
          <text className="research-figure__muted" x="430" y="32">
            authority boundary
          </text>

          <rect className="research-figure__box" x="145" y="225" width="510" height="82" rx="8" />
          <text className="research-figure__label" x="400" y="257" textAnchor="middle">
            Durable state, audit evidence, and recovery log
          </text>
          <text className="research-figure__muted" x="400" y="282" textAnchor="middle">
            versions · approvals · attempts · effects · terminal status
          </text>
          <line
            className="research-figure__line"
            x1="317"
            y1="139"
            x2="317"
            y2="217"
            markerEnd="url(#controlled-agent-arrow)"
          />
          <line
            className="research-figure__line"
            x1="522"
            y1="159"
            x2="522"
            y2="217"
            markerEnd="url(#controlled-agent-arrow)"
          />
        </svg>
        <figcaption>
          Figure 1. The model remains inside the proposal path. Policy,
          credentials, approval, and tool enforcement define the authority
          boundary; durable evidence supports recovery and accountability.
        </figcaption>
      </figure>

      <h3>Bounded Autonomy and Tool Authority</h3>
      <p>
        Autonomy is a vector, not a binary label: scope of eligible cases,
        action repertoire, monetary or operational limits, time horizon,
        reversibility, and approval requirements can each be constrained.
        Begin with recommendation or draft generation; permit execution only
        for low-impact, reversible actions after evidence from shadow operation.
        Capability must exceed neither policy nor credential scope. Short-lived,
        case-bound credentials should restrict tool, record, tenant,
        destination, rate, and value. The gateway authenticates the originating
        principal, validates arguments against schemas and business invariants,
        and denies unknown operations by default. Prompt text, retrieved
        instructions, and model-generated “approval” are data, never authority.
      </p>
      <p>
        This separation addresses a security asymmetry: an agent must process
        untrusted content to perform useful work, yet that content can redirect
        subsequent tool use. OWASP classifies prompt injection, excessive
        agency, and insecure output handling as application risks; AgentDojo
        provides an environment for evaluating utility and attack resistance
        together. Input filtering alone is insufficient because indirect
        instructions may be semantically entangled with legitimate records.
        Least privilege and independent effect validation limit harm even when
        model behavior is compromised.
      </p>

      <h3>State, Recovery, and Exactly-Once Intent</h3>
      <p>
        Conversation history is not authoritative workflow state. Persist a
        typed state machine containing the case identifier, source provenance,
        model and prompt versions, policy decision, approval artifact, tool
        request and response, attempt count, and terminal status. Each external
        action needs an idempotency key and an effect check. Retries require
        bounded budgets and backoff; they must not duplicate payments,
        notifications, or access changes. If atomic rollback is impossible,
        define compensating actions and identify residual harm explicitly.
        Timeouts, cancellation, stale approvals, partial completion, and tool
        unavailability are first-class transitions, not exceptional prose sent
        back to the model.
      </p>

      <h3>Evaluation as a Safety Case</h3>
      <p>
        Model benchmarks establish neither workflow reliability nor acceptable
        risk. A versioned evaluation set should sample normal cases, boundary
        cases, rare high-impact failures, distribution shifts, conflicting
        records, permission changes, unavailable tools, duplicate events, and
        direct and indirect prompt injection. Evaluate component properties
        separately—retrieval provenance, decision quality, tool choice,
        argument validity, policy enforcement, abstention, recovery—and then
        evaluate the complete trajectory. AgentBench provides one research
        precedent for interactive evaluation; ToolEmu contributes an approach
        to emulating tool-use risks without real-world effects.
      </p>
      <p>
        Compare against the current process and a simpler deterministic
        baseline. Report task success alongside unauthorized-action rate,
        severe-hazard count, recovery success, rework, calibrated abstention,
        latency, and subgroup error where decisions affect people. Do not
        collapse a catastrophic policy breach into an average score. Release
        gates should specify zero-tolerance hazards where appropriate, minimum
        sample coverage, uncertainty intervals, and reviewer adjudication.
        Model-based grading is useful for scale only after calibration against
        blinded domain review and periodic checks for grader drift.
      </p>

      <h3>Observability and Human Oversight</h3>
      <p>
        Observability must reconstruct why an effect occurred without exposing
        unnecessary sensitive data. Correlate case, policy, model, prompt,
        retrieval, approval, and tool-event identifiers; record structured
        decisions rather than unrestricted hidden reasoning. Protect logs with
        access control, minimization, redaction, integrity checks, and retention
        limits. Monitor policy denials, overrides, unresolved states, retry
        storms, tool-schema failures, input drift, and outcome drift. A kill
        switch must revoke credentials and stop dispatch, not merely add a
        natural-language instruction.
      </p>
      <p>
        Human oversight is effective only when intervention is informed,
        timely, and consequential. Reviewers need the proposed action, material
        evidence, uncertainty or conflict indicators, expected effect, and a
        usable refuse or amend path. Assign a named service owner and incident
        commander; separate routine approval from independent audit. Escalation
        capacity must be tested under peak load because overloaded reviewers
        can become a rubber stamp. Deployment should progress through replay,
        shadowing, assisted operation, and narrowly scoped execution, with
        rollback criteria defined before each transition.
      </p>

      <h3>Assumptions and Threats to Validity</h3>
      <p>
        The framework assumes tools expose enforceable interfaces, identities
        can be authenticated, consequential effects are detectable, workflow
        owners can define unacceptable outcomes, and the enterprise can revoke
        access promptly. It also assumes that at least some failures are
        recoverable and representative historical or simulated cases are
        available. Where these assumptions fail—especially for irreversible
        physical, legal, safety, or rights-affecting decisions—the justified
        autonomy envelope may be negligible.
      </p>
      <p>
        Threats to validity include incomplete hazard discovery, sparse
        evidence for rare events, simulation-to-production mismatch,
        non-stationary users and attacks, benchmark contamination, and changes
        in upstream models or tools. Production monitoring observes only
        deployed cases and may miss rejected or displaced harms. Human review
        introduces automation bias and organizational incentives that technical
        metrics do not capture. The model also abstracts away multi-agent
        coordination, shared-memory races, and cross-jurisdictional compliance.
        Consequently, the framework supports a revisable safety case, not a
        universal certification.
      </p>

      <h3>Conclusion</h3>
      <p>
        A trustworthy enterprise agent is a fallible decision component inside
        a controlled, observable, and recoverable system. Trust follows from
        constraining effects, independently enforcing authority, preserving
        state and evidence, testing complete trajectories under attack and
        failure, and maintaining meaningful human accountability. Autonomy
        should expand only when workflow-specific evidence justifies the added
        exposure.
      </p>

      <h3>References</h3>
      <ol>
        <li>
          National Institute of Standards and Technology,{" "}
          <a href="https://doi.org/10.6028/NIST.AI.100-1">
            Artificial Intelligence Risk Management Framework (AI RMF 1.0)
          </a>
          , 2023.
        </li>
        <li>
          National Institute of Standards and Technology,{" "}
          <a href="https://doi.org/10.6028/NIST.AI.600-1">
            Artificial Intelligence Risk Management Framework: Generative
            Artificial Intelligence Profile
          </a>
          , 2024.
        </li>
        <li>
          OWASP Foundation,{" "}
          <a href="https://genai.owasp.org/llm-top-10/">
            OWASP Top 10 for Large Language Model Applications
          </a>
          , official project.
        </li>
        <li>
          Shunyu Yao et al.,{" "}
          <a href="https://openreview.net/forum?id=WE_vluYUL-X">
            ReAct: Synergizing Reasoning and Acting in Language Models
          </a>
          , ICLR 2023.
        </li>
        <li>
          Xiao Liu et al.,{" "}
          <a href="https://openreview.net/forum?id=zAdUB0aCTQ">
            AgentBench: Evaluating LLMs as Agents
          </a>
          , ICLR 2024.
        </li>
        <li>
          Zifan Ruan et al.,{" "}
          <a href="https://openreview.net/forum?id=4VX1p4zO9H">
            ToolEmu: Identifying the Risks of LM Agents with an LM-Emulated
            Sandbox
          </a>
          , ICLR 2024.
        </li>
        <li>
          Edoardo Debenedetti et al.,{" "}
          <a href="https://proceedings.neurips.cc/paper_files/paper/2024/hash/7f1aaf96c59cb3a4903e7f8c132fcea0-Abstract-Datasets_and_Benchmarks_Track.html">
            AgentDojo: A Dynamic Environment to Evaluate Prompt Injection
            Attacks and Defenses for LLM Agents
          </a>
          , NeurIPS 2024.
        </li>
        <li>
          National Institute of Standards and Technology,{" "}
          <a href="https://csrc.nist.gov/pubs/sp/800/53/r5/upd1/final">
            Security and Privacy Controls for Information Systems and
            Organizations (SP 800-53 Rev. 5)
          </a>
          , 2020, updated 2023.
        </li>
      </ol>
    </div>
  );

  return { ...metadata, content };
};

export default AIEmployeesPost;
